import React, { useState } from "react";
import {
  Box,
  Button,
  TextField,
  MenuItem,
  Typography,
  Tooltip,
  Stack,
} from "@mui/material";
import {
  DataGrid,
  GridActionsCellItem,
  GridRowEditStopReasons,
  GridRowModes,
} from "@mui/x-data-grid";
import {
  Add,
  Cancel,
  Delete,
  Edit,
  Save,
  Upload,
} from "@mui/icons-material";
import * as XLSX from "xlsx";
import { useDropzone } from "react-dropzone";
import toast from "react-hot-toast";
import {
  departmentMap,
  genderMap,
  jobGradeMap,
  roleMap,
} from "../../data/userColumn";
import { bannedDomains } from "../../data/bannedDomains";
import ExportEmployeeExcel from "./exportEmployeeExcel";
import parseDate from "../../helper/parsedDate";

// Custom Components
const CustomNoRowsOverlay = ({ onAdd, hasData, filter }) => {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
    },
    multiple: false,
    onDrop: (acceptedFiles) => {
      if (acceptedFiles?.length > 0) {
        const fakeEvent = { target: { files: acceptedFiles } };
        onAdd(fakeEvent);
      }
    },
  });


  if (hasData) {
    return (
      <Box
        sx={{
          height: "100%",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          p: 4,
        }}
      >
        <Typography variant="body" fontWeight="700">
          {filter === "invalid"
            ? "No invalid data 🎉"
            : filter === "valid"
              ? "No valid data found"
              : "No records to display"}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      {...getRootProps()}
      sx={{
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        border: "2px dashed #ccc",
        borderRadius: 2,
        p: 4,
        cursor: "pointer",
        backgroundColor: isDragActive ? "#f5f5f5" : "transparent",
      }}
    >
      <input {...getInputProps()} />
      <Typography
        variant="subtitle"
        fontWeight={"700"}
        sx={{
          display: "flex",
          alignItems: "center",
          alignContent: "center",
          gap: 2,
        }}
      >
        Drag and drop or upload a files to get start
      </Typography>
      <Stack direction={'row'} spacing={2} justifyContent={'center'} pt={2}>
        {/* <ExportEmployeeExcel color={'secondary.main'} /> */}
        <Button
          variant="contained"
          startIcon={<Upload />}
          size="large"
          sx={{
            backgroundColor: "secondary.main",
            mt: 2,
            px: 7,
            py: 1,
            textTransform: "none",
            fontSize: "14px",
          }}
        >
          Upload File
        </Button>
      </Stack>
    </Box>
  );
};

const EditToolbar = ({ onAddNewRecord, setRowModesModel, total, active, onAll, onValid, onInvalid, validCount, invalidCount }) => {
  const handleClick = () => {
    // Call the parent component's add function
    onAddNewRecord();
  };

  return (
    <Stack direction={'row'} justifyContent={'space-between'} sx={{ p: 1 }}>
      <Button onClick={handleClick} startIcon={<Add />}>
        Add Record
      </Button>

      <Stack direction={'row'} alignItems={'center'}>
        <Typography variant="body" pr={4}>
          {total} records
        </Typography>
        <Box
          sx={{
            display: "flex",
            borderRadius: "8px",
            backgroundColor: "#bcbec07d",
            p: "6px",
            gap: "4px",
          }}
        >
          <Button
            variant={active === "all" ? "contained" : "body"}
            onClick={onAll}
            sx={{ minWidth: 80 }}
          >
            All&nbsp;{total}
          </Button>
          <Button
            variant={active === "valid" ? "contained" : "body"}
            onClick={onValid}
            sx={{ minWidth: 80 }}
          >
            Valid&nbsp;{validCount}
          </Button>
          <Button
            variant={active === "invalid" ? "contained" : "body"}
            onClick={onInvalid}
            sx={{ minWidth: 80 }}
          >
            Invalid&nbsp;{invalidCount}
          </Button>
        </Box>
      </Stack>
    </Stack>
  );
}

const formatDateDDMMYYYY = (date) => {
  if (!(date instanceof Date) || isNaN(date.getTime())) return "";
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const validateField = (field, value) => {
  switch (field) {
    case "fullname":
      if (!value?.trim()) return "Full name is required";
      return "";
    case "email":
      if (!value?.trim()) return "Email is required";
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) return "Invalid email format";
      const domain = value.split("@")[1];
      if (bannedDomains.includes(domain)) {
        return `Emails from ${domain} are not allowed. Use your work email.`;
      }
      return "";
    case "dob":
      if (!value) return "Date of birth is required";
      if (isNaN(new Date(value).getTime())) return "Invalid date";
      return "";
    case "designation":
      if (!value?.trim()) return "Designation is required";
      return "";
    case "employeecode":
      if (!value?.trim()) return "Employee Code is required";
      if (!/^[a-zA-Z0-9]+$/.test(value)) return "Only letters & numbers allowed";
      return "";
    default:
      return "";
  }
};

const validateRow = (row, allRows = []) => {
  const errors = {};

  if (!row.fullname?.trim()) errors.fullname = "Full name is required";
  if (!row.employeecode?.trim()) errors.employeecode = "Employee code is required";

  // Email validation
  if (!row.email?.trim()) {
    errors.email = "Email is required";
  } else {
    const email = row.email.trim().toLowerCase();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      errors.email = "Invalid email format";
    } else {
      const domain = email.split("@")[1];
      if (bannedDomains.includes(domain)) {
        errors.email = `Emails from ${domain} are not allowed. Use your work email.`;
      } else {
        // Check for duplicate email across all rows
        const duplicate = allRows.some(
          (r) => r.id !== row.id && r.email?.trim().toLowerCase() === email
        );
        if (duplicate) {
          errors.email = "This email is already in use";
        }
      }
    }
  }

  if (!row.gender) errors.gender = "Gender is required";
  if (!row.dob) {
    errors.dob = "Date of birth is required";
  } else if (isNaN(new Date(row.dob).getTime())) {
    errors.dob = "Invalid date";
  }

  if (!row.role) errors.role = "Role is required";
  if (!row.designation) errors.designation = "Designation is required";
  if (!row.department) errors.department = "Department is required";
  if (!row.jobgrade) errors.jobgrade = "Job grade is required";

  return errors;
};

// Main Component
const UploadGridUser = ({ onDataChange, onJobData }) => {
  const [rowModesModel, setRowModesModel] = useState({});
  const [allRows, setAllRows] = useState([]);        // All records from file
  const [validRows, setValidRows] = useState([]);    // Only valid records
  const [invalidRows, setInvalidRows] = useState([]); // Only invalid records
  const [displayRows, setDisplayRows] = useState([]); // Currently displayed rows
  const [filter, setFilter] = useState("all");

  const isValidRow = (row) => Object.keys(row.errors || {}).length === 0;

  const [departments, setDepartments] = useState(Object.values(departmentMap));
  const [roles, setRoles] = useState(Object.values(roleMap));
  const [jobGrades, setJobGrades] = useState(Object.values(jobGradeMap));
  const [genders, setGenders] = useState(Object.values(genderMap));


  const handleAddNewRecord = () => {
    const newId = allRows.length > 0 ? Math.max(...allRows.map(r => r.id)) + 1 : 1;
    const newRow = {
      id: newId,
      no: newId,
      employeeCode: "",
      fullname: "",
      role: "",
      email: "",
      gender: "",
      dob: "",
      designation: "",
      department: "",
      jobgrade: "",
      errors: {},
      isNew: true
    };

    const updatedAllRows = [...allRows, newRow];
    handleDataChange(updatedAllRows);
    setRowModesModel((oldModel) => ({
      ...oldModel,
      [newId]: { mode: GridRowModes.Edit, fieldToFocus: "fullname" },
    }));
  };


  const updateAllDataArrays = (rows) => {
  // Validate all rows including duplicate email check
  const validatedRows = rows.map((row) => ({
    ...row,
    errors: validateRow(row, rows), // pass all rows for duplicate check
  }));

  setAllRows(validatedRows);
  setValidRows(validatedRows.filter((r) => isValidRow(r)));
  setInvalidRows(validatedRows.filter((r) => !isValidRow(r)));
  applyFilter(filter, validatedRows);
};

  const applyFilter = (filterType, rows = allRows) => {
    let filtered;

    if (filterType === "all") {
      filtered = rows;
    } else if (filterType === "valid") {
      filtered = rows.filter((r) => isValidRow(r));
    } else if (filterType === "invalid") {
      filtered = rows.filter((r) => !isValidRow(r));
    }

    setDisplayRows(filtered || []);
  };

  const total = allRows.length;
  const validCount = validRows.length;
  const invalidCount = invalidRows.length;

  const handleFilterAll = () => {
    setFilter("all");
    applyFilter("all");
  };

  const handleFilterValid = () => {
    setFilter("valid");
    applyFilter("valid");
  };

  const handleFilterInvalid = () => {
    setFilter("invalid");
    applyFilter("invalid");
  };

  // When file upload or row updates happen:
  const handleDataChange = (rows) => {
    updateAllDataArrays(rows);
    applyFilter(filter, rows);
    onDataChange?.(rows);
  };

  const makeHandleChange = (params, field) => (e) => {
    const value = e.target.value;
    const errorMessage = validateField(field, value);

    params.api.setEditCellValue({
      id: params.id,
      field,
      value,
    });

    // Update the specific row in allRows
    const updatedAllRows = allRows.map((row) =>
      row.id === params.id
        ? { ...row, [field]: value, errors: { ...(row.errors || {}), [field]: errorMessage } }
        : row
    );

    handleDataChange(updatedAllRows);
  };


  const handleEditClick = (id) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleSaveClick = (id) => () => {
    const row = allRows.find((r) => r.id === id);
    if (!row) return;

    const errors = validateRow(row, allRows);

    if (Object.keys(errors).length > 0) {
      const updatedRow = { ...row, errors };
      const updatedAllRows = allRows.map((r) => (r.id === id ? updatedRow : r));
      handleDataChange(updatedAllRows);
      toast.error("Please fix errors before saving.");
      return;
    }

    const updatedRow = { ...row, errors: {} };
    const updatedAllRows = allRows.map((r) => (r.id === id ? updatedRow : r));

    handleDataChange(updatedAllRows);
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });

    // Force re-apply the current filter after saving
    applyFilter(filter, updatedAllRows);
  };

  const handleDeleteClick = (id) => () => {
    const updatedAllRows = allRows.filter((row) => row.id !== id);
    handleDataChange(updatedAllRows);
  };

  const handleCancelClick = (id) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });

    const editedRow = allRows.find((row) => row.id === id);
    if (editedRow?.isNew) {
      const updatedAllRows = allRows.filter((row) => row.id !== id);
      handleDataChange(updatedAllRows);
    }
  };

  const processRowUpdate = (newRow) => {
    const errors = validateRow(newRow, allRows);
    const updatedRow = { ...newRow, errors };
    const updatedAllRows = allRows.map((r) => (r.id === newRow.id ? updatedRow : r));
    handleDataChange(updatedAllRows);

    // Re-apply current filter after update
    applyFilter(filter, updatedAllRows);

    if (Object.keys(errors).length > 0) {
      toast.error("Fix validation errors before saving.");
    }

    return updatedRow;
  };

  const handleRowEditStop = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };



  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const reader = new FileReader();
      reader.onload = (e) => {
        const data = e.target.result;
        const workbook = XLSX.read(data, { type: "binary" });

        // 🟢 STEP 1: Read the SECOND sheet first for Job Grades
        if (workbook.SheetNames.length > 1) {
          const jobGradeSheet = workbook.Sheets[workbook.SheetNames[1]];
          const jobGradeRows = XLSX.utils.sheet_to_json(jobGradeSheet, { header: 1, defval: "" });

          // 🟢 Skip first 7 rows (row index 0–6) → start reading from row 7 (the 8th row in Excel)
          const relevantRows = jobGradeRows.slice(7);

          // 🟢 Extract only columns B and C (index 1 and 2)
          const filteredRows = relevantRows.map((row) => [row[1], row[2]]).filter(r => r[0] || r[1]);

          if (filteredRows.length === 0) {
            console.warn("⚠️ No job grade data found from row 7 and above — skipping.");
            // return;
          }

          // 🧩 Map to objects
          const jobGradeData = filteredRows.map((row) => ({
            name: row[0]?.toString().trim() || "",
            description: row[1]?.toString().trim() || "",
          })).filter(j => j.name || j.description);

          // console.log("🟢 formattedGrades:", jobGradeData);
          onJobData(jobGradeData);
        } else {
          console.warn("⚠️ Only one sheet found — skipping job grade upload.");
        }

        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const rows = XLSX.utils.sheet_to_json(sheet, { header: 1 });

        const headerRowIndex = findHeaderRow(rows);

        if (headerRowIndex === -1) {
          toast.error("Invalid file format: Header row not found.");
          return;
        }

        const headers = rows[headerRowIndex];
        const filteredData = rows.slice(headerRowIndex + 1); // Data starts after headers

        // 🔑 Step 1: Expected headers from your grid
        const expectedHeaders = userColumns
          .filter((col) => col.field !== "id" && col.field !== "actions") // exclude auto/id/action fields
          .map((col) =>
            col.field
              .toLowerCase()
              .replace(/\s+/g, "")
              .replace(/\W+/g, "")
          );
        // console.log('expected headers', expectedHeaders);

        // 🔑 Step 2: Clean uploaded headers

        const uploadedHeaders = headers.map((h) =>
          (h || "")
            .toLowerCase()
            .replace(/\s+/g, "")
            .replace(/\W+/g, "")
        );

        // console.log('uploaded headers', uploadedHeaders);


        // 🔑 Step 3: Compare
        const normalizedUploaded = uploadedHeaders.map(h =>
          h.toLowerCase().split("eg")[0].trim()
        );

        const missing = expectedHeaders.filter(col => !normalizedUploaded.includes(col));

        if (missing.length > 0) {
          toast.error(`Invalid file format. Missing: ${missing.join(", ")}`);
          return;
        }

        // ✅ Proceed if headers match
        const mappedRows = filteredData
          .filter(row => row.some(cell => cell !== null && cell !== undefined && cell !== "")) // Remove empty rows
          .map((row, idx) => {
            const obj = { id: idx + 1 };
            normalizedUploaded.forEach((key, colIdx) => {
              if (key) {
                const cleanKey = key
                  .toLowerCase()
                  .replace(/\s+/g, "")
                  .replace(/\W+/g, "");

                let value = row[colIdx] || "";
                // console.log('value', value);

                if (cleanKey === "dob" && value) {
                  obj[cleanKey] = parseDate(value);
                } else {
                  obj[cleanKey] = value;
                }
              }
            });

            obj.errors = validateRow(obj);
            return obj;
          });
        // console.log("Mapped Rows:", mappedRows);

        // 🟢 Now validate with all rows at once
        const validatedRows = mappedRows.map((row) => ({
          ...row,
          errors: validateRow(row, mappedRows), // pass the full array
        }));

        handleDataChange(validatedRows);
        // handleDataChange(mappedRows);
      };
      reader.readAsBinaryString(file);
    } catch (error) {
      console.error("File upload error:", error);
      toast.error("Failed to upload file");
    }
  };


  const findHeaderRow = (rows) => {
    // Common header keywords to look for (add more as needed)
    const headerKeywords = [
      'no', 'number', 'employee code', 'code', 'full name', 'name',
      'role', 'email', 'gender', 'dob', 'date of birth', 'birthdate',
      'designation', 'department', 'job grade', 'grade'
    ].map(h => h.toLowerCase().replace(/\s+/g, "").replace(/\W+/g, ""));

    // Search from row 0 to row 10 (adjust range as needed)
    for (let i = 0; i < Math.min(rows.length, 10); i++) {
      const row = rows[i] || [];

      // Convert row cells to normalized strings
      const normalizedRow = row.map(cell =>
        (cell || "").toString().toLowerCase().replace(/\s+/g, "").replace(/\W+/g, "")
      ).filter(cell => cell.length > 0); // Remove empty cells

      // Count how many header keywords are found in this row
      const matchCount = normalizedRow.filter(cell =>
        headerKeywords.some(keyword => cell.includes(keyword) || keyword.includes(cell))
      ).length;

      // If we found a significant number of matches, this is likely the header row
      if (matchCount >= 3) { // Adjust threshold as needed
        return i;
      }
    }

    // Fallback: Look for a row that has multiple non-empty cells
    for (let i = 0; i < Math.min(rows.length, 10); i++) {
      const row = rows[i] || [];
      const nonEmptyCells = row.filter(cell =>
        cell !== null && cell !== undefined && cell.toString().trim() !== ""
      );

      if (nonEmptyCells.length >= 5) { // Row with at least 5 non-empty cells
        return i;
      }
    }

    return -1; // No header row found
  };

  // Optional: More specific header detection for your known formats
  const findHeaderRowAdvanced = (rows) => {
    // Try specific known patterns first
    const knownHeaderPatterns = [
      // Pattern for "Correct Format"
      ['no', 'employeecode', 'fullname', 'role', 'email', 'gender', 'dob', 'designation', 'department', 'jobgrade'],
      // Pattern for "Wrong Format"  
      ['no', 'code', 'name', 'role', 'email', 'gender', 'dob', 'designations', 'departments', 'jobgrade'],
      // Pattern for "Wrong Start Row"
      ['code', 'name', 'role', 'email', 'gender', 'dob', 'designations', 'departments', 'jobgrade']
    ].map(pattern => pattern.map(p => p.toLowerCase()));

    for (let i = 0; i < Math.min(rows.length, 10); i++) {
      const row = rows[i] || [];
      const normalizedRow = row.map(cell =>
        (cell || "").toString().toLowerCase().replace(/\s+/g, "").replace(/\W+/g, "")
      );

      for (const pattern of knownHeaderPatterns) {
        const matchCount = pattern.filter((header, index) =>
          normalizedRow[index] === header
        ).length;

        if (matchCount >= pattern.length * 0.7) { // 70% match threshold
          return i;
        }
      }
    }

    // Fall back to the basic method
    return findHeaderRow(rows);
  };


  // Column Definitions
  const userColumns = [
    {
      field: "id",
      headerName: "#",
      width: 50,
    },
    {
      field: "employeecode",
      headerName: "Employee Code",
      minWidth: 150,
      editable: true,
      renderEditCell: (params) => {
        const errorMessage = params.row.errors?.employeecode || "";
        return (
          <Tooltip title={errorMessage} placement="top" disableInteractive>
            <TextField
              fullWidth
              value={params.value || ""}
              onChange={makeHandleChange(params, "employeecode")}
              error={!!errorMessage}
            />
          </Tooltip>
        );
      },
    },
    {
      field: "fullname",
      headerName: "Full Name",
      minWidth: 200,
      flex: 1,
      editable: true,
      renderCell: (params) => (
        <Box
          sx={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            width: "100%",
          }}
        >
          {params.value}
        </Box>
      ),
      renderEditCell: (params) => {
        const errorMessage = params.row.errors?.fullname || "";
        return (
          <Tooltip title={errorMessage} placement="top" disableInteractive>
            <TextField
              fullWidth
              value={params.value || ""}
              onChange={makeHandleChange(params, "fullname")}
              error={!!errorMessage}
            />
          </Tooltip>
        );
      },
    },
    {
      field: "role",
      headerName: "Role",
      minWidth: 150,
      editable: true,
      type: "singleSelect",
      valueOptions: roles,
      renderCell: (params) => (
        <Box sx={{ textTransform: "capitalize" }}>
          {params.value}
        </Box>
      ),
      renderEditCell: (params) => {
        const errorMessage = params.row.errors?.role || "";
        return (
          <Tooltip title={errorMessage} placement="top" disableInteractive>
            <TextField
              select
              fullWidth
              value={params.value || ""}
              onChange={makeHandleChange(params, "role")}
              error={!!errorMessage}
            >
              {roles.map((data) => (
                <MenuItem key={data} value={data}>
                  {data}
                </MenuItem>
              ))}
            </TextField>
          </Tooltip>
        );
      },
    },
    {
      field: "email",
      headerName: "Email",
      minWidth: 200,
      flex: 1,
      editable: true,
      renderCell: (params) => (
        <Box
          sx={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            width: "100%",
          }}
        >
          {params.value}
        </Box>
      ),
      renderEditCell: (params) => {
        const errorMessage = params.row.errors?.email || "";
        return (
          <Tooltip title={errorMessage} placement="top" disableInteractive>
            <TextField
              fullWidth
              value={params.value || ""}
              onChange={makeHandleChange(params, "email")}
              error={!!errorMessage}
            />
          </Tooltip>
        );
      },
    },
    {
      field: "gender",
      headerName: "Gender",
      minWidth: 150,
      editable: true,
      type: "singleSelect",
      valueOptions: genders,
      renderCell: (params) => (
        <Box sx={{ textTransform: "capitalize" }}>
          {params.value}
        </Box>
      ),
      renderEditCell: (params) => {
        const errorMessage = params.row.errors?.gender || "";
        return (
          <Tooltip title={errorMessage} placement="top" disableInteractive>
            <TextField
              select
              fullWidth
              value={params.value || ""}
              onChange={makeHandleChange(params, "gender")}
              error={!!errorMessage}
            >
              {genders.map((data) => (
                <MenuItem key={data} value={data}>
                  {data}
                </MenuItem>
              ))}
            </TextField>
          </Tooltip>
        );
      },
    },
    {
      field: "dob",
      headerName: "Date of Birth",
      minWidth: 150,
      flex: 1,
      editable: true,
      renderEditCell: (params) => {
        const errorMessage = params.row.errors?.dob || "";
        return (
          <Tooltip title={errorMessage} placement="top" disableInteractive>
            <TextField
              type="date"
              fullWidth
              value={
                params.value
                  ? new Date(params.value).toISOString().split("T")[0]
                  : ""
              }
              onChange={makeHandleChange(params, "dob")}
              error={!!errorMessage}
            />
          </Tooltip>
        );
      },
    },
    {
      field: "designation",
      headerName: "Designation",
      minWidth: 150,
      editable: true,
      renderEditCell: (params) => {
        const errorMessage = params.row.errors?.designation || "";
        return (
          <Tooltip title={errorMessage} placement="top" disableInteractive>
            <TextField
              fullWidth
              value={params.value || ""}
              onChange={makeHandleChange(params, "designation")}
              error={!!errorMessage}
            />
          </Tooltip>
        );
      },
    },
    {
      field: "department",
      headerName: "Department",
      minWidth: 150,
      editable: true,
      renderEditCell: (params) => {
        const errorMessage = params.row.errors?.department || "";
        return (
          <Tooltip title={errorMessage} placement="top" disableInteractive>
            <TextField
              fullWidth
              value={params.value || ""}
              onChange={makeHandleChange(params, "department")}
              error={!!errorMessage}
            />
          </Tooltip>
        );
      },
    },
    {
      field: "jobgrade",
      headerName: "Job Grade",
      minWidth: 150,
      editable: true,
      renderEditCell: (params) => {
        const errorMessage = params.row.errors?.jobgrade || "";
        return (
          <Tooltip title={errorMessage} placement="top" disableInteractive>
            <TextField
              fullWidth
              value={params.value || ""}
              onChange={makeHandleChange(params, "jobgrade")}
              error={!!errorMessage}
            />
          </Tooltip>
        );
      },
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 100,
      cellClassName: "actions",
      getActions: ({ id }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              icon={<Save />}
              label="Save"
              sx={{ color: "primary.main" }}
              onClick={handleSaveClick(id)}
            />,
            <GridActionsCellItem
              icon={<Cancel />}
              label="Cancel"
              color="inherit"
              onClick={handleCancelClick(id)}
            />,
          ];
        }

        return [
          <GridActionsCellItem
            icon={<Edit />}
            label="Edit"
            color="inherit"
            onClick={handleEditClick(id)}
          />,
          <GridActionsCellItem
            icon={<Delete />}
            label="Delete"
            color="inherit"
            onClick={handleDeleteClick(id)}
          />,
        ];
      },
    },
  ];

  const columnNameMap = Object.fromEntries(
    userColumns.map(col => [col.field.toLowerCase(), col.headerName])
  );

  return (
    <Box m="8px 0 0 0" style={{ height: 680, width: "100%" }}>
      <DataGrid
        columns={userColumns}
        rows={displayRows}
        getCellClassName={(params) => {
          const rowErrors = params.row.errors || {};
          return rowErrors[params.field] ? "cell-error" : "";
        }}
        editMode="row"
        rowModesModel={rowModesModel}
        onRowModesModelChange={setRowModesModel}
        onRowEditStop={handleRowEditStop}
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={(error) => {
          console.error("Row update error:", error);
          toast.error(`Row has validation errors. Please fix them before saving.`);
        }}
        showToolbar
        pageSizeOptions={[5, 10, 50]}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 10 },
          },
        }}
        slots={{
          toolbar: EditToolbar,
          noRowsOverlay: () => (
            <CustomNoRowsOverlay
              onAdd={handleFileUpload}
              hasData={allRows.length > 0}
              filter={filter}
            />
          ),
        }}
        slotProps={{
          toolbar: {
            onAddNewRecord: handleAddNewRecord, // Add this function
            setRowModesModel,
            total,
            active: filter,
            onAll: handleFilterAll,
            onValid: handleFilterValid,
            onInvalid: handleFilterInvalid,
            validCount,
            invalidCount
          },
        }}
        // checkboxSelection
        disableRowSelectionOnClick
        sx={{
          border: 1,
          borderColor: "divider",
          borderRadius: 0,
          "& .MuiDataGrid-row": {
            backgroundColor: "transparent",
            borderBottom: "1px solid #ccc !important",
          },
          "& .MuiDataGrid-cell": {
            borderRight: "1px solid #ccc !important",
          },
          "& .MuiDataGrid-columnHeaders": {
            borderBottom: "1px solid #ccc !important",
          },
        }}
      />
    </Box>
  );
};

export default UploadGridUser;